/**
 * Plain-language helpers that translate betting jargon for first-time bettors.
 * Decimal odds → implied chance, potential return, and human market labels.
 */

/** Implied win chance (0-1) from decimal odds, inclusive of the bookmaker margin. */
export function impliedChance(odds: number): number {
  if (!odds || odds <= 1) return 0;
  return 1 / odds;
}

/** What a R{stake} bet returns IN TOTAL (stake + profit) at these odds. */
export function totalReturn(odds: number, stakeRands = 100): number {
  return Math.round(odds * stakeRands);
}

/** The PROFIT (winnings on top of stake) for a R{stake} bet. */
export function profit(odds: number, stakeRands = 100): number {
  return Math.round(odds * stakeRands - stakeRands);
}

/** Format rands without decimals: 145 → "R145". */
export function rands(n: number): string {
  return `R${Math.round(n).toLocaleString('en-ZA')}`;
}

/** Rank selections by odds → favourite (shortest) to underdog (longest). */
export type FavTag = 'favourite' | 'underdog' | null;
export function favouriteTag(odds: number, allOdds: number[]): FavTag {
  if (allOdds.length < 2) return null;
  const min = Math.min(...allOdds);
  const max = Math.max(...allOdds);
  if (odds === min) return 'favourite';
  if (odds === max) return 'underdog';
  return null;
}

/**
 * Human-friendly market metadata. Given the raw market name/type, return a
 * newcomer-readable title, a one-line "what is this?" explainer, and a
 * per-selection label transformer.
 */
export interface MarketCopy {
  title: string;
  help: string;
  /** Transform a raw selection name into a friendly label given event context. */
  selectionLabel: (name: string, home: string, away: string) => string;
}

export function marketCopy(
  marketType: string,
  marketName: string,
  home: string,
  away: string,
): MarketCopy {
  const t = (marketType || '').toLowerCase();
  const n = (marketName || '').toLowerCase();

  if (t === 'match_winner' || n.includes('match winner') || n.includes('1x2')) {
    return {
      title: 'Who will win?',
      help: 'Pick the team you think wins the match. "Draw" means the match ends level.',
      selectionLabel: (name) => {
        if (name.toLowerCase() === 'draw') return 'Draw';
        return name; // already a team name
      },
    };
  }

  if (t === 'over_under' || n.includes('over') || n.includes('total')) {
    return {
      title: 'Total goals — more or fewer?',
      help:
        'Bet on the combined goals scored by BOTH teams. "Over 2.5" wins if 3+ goals are scored in total; "Under 2.5" wins if 2 or fewer.',
      selectionLabel: (name) => {
        // Names look like "Over 2.5" / "Under 2.5"
        const m = name.match(/(over|under)\s*([\d.]+)/i);
        if (!m) return name;
        const dir = m[1].toLowerCase() === 'over' ? 'More than' : 'Fewer than';
        return `${dir} ${m[2]} goals`;
      },
    };
  }

  // Fallback — show the raw name with a generic helper.
  return {
    title: marketName,
    help: 'Pick an outcome below. The number is your odds — it multiplies your stake if you win.',
    selectionLabel: (name) => name,
  };
}
